import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ManualEventForm.css';
import './desktop-event-form.css'; // Import desktop specific styles
import { db } from '../firebase';
import { collection, getDoc, doc, updateDoc } from 'firebase/firestore';
import TimeInputModal from './TimeInputModal';
import { useFamilyParticipants } from '../hooks/useFamilyParticipants';
import { removeFamilyMember, getUserFamilyInfo, addCustomParticipantToFamily, removeCustomParticipantFromFamily } from '../services/familyService';
import { useAuth } from '../contexts/AuthContext';
import { useClassRole } from '../hooks/useClassRole';

/* MODAL STYLES - CSS for edit event popup overlay and positioning */
/* Defines how the modal appears on both mobile and desktop */
const modalStyles = `
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  padding-top: 12vh; /* Increased top padding to position modal higher in viewport */
}

.modal-close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white;
}

.neo-form-header {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  background-color: #2A9D8F;
  color: white;
}

.neo-form-content {
  padding: 15px;
  padding-bottom: 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* Smoother scrolling on iOS */
  max-height: calc(80vh - 70px); /* 80% of viewport height minus header */
}

.modal-container {
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  max-width: 550px;
  width: 90%;
  position: relative;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding-top: 5vh; /* Position modal higher in the viewport for mobile */
    align-items: flex-start;
  }
  
  .modal-container {
    width: 98%;
    max-height: 75vh; /* Reduced height to ensure it fits better in viewport */
    margin-bottom: 30px; /* Added margin to ensure buttons are visible */
  }

  .neo-form-content {
    max-height: calc(75vh - 70px);
    overflow-y: auto;
    padding-bottom: 30px; /* Reduced bottom padding for mobile */
  }
}
`;

/* EDIT EVENT MODAL COMPONENT - Popup form for editing existing events */
/* Used throughout the app when user clicks "Edit Event" on any event card */
/* Provides a comprehensive form with all event fields and recurrence options */
const EditEventModal = ({
  event: propEvent,
  onSave: propOnSave,
  onClose,
  isOpen
}) => {
  const [event, setEvent] = useState(propEvent || null);
  const { participants: familyParticipants, refreshParticipants } = useFamilyParticipants();
  
  const { currentUser } = useAuth();
  const { isClassAdmin, classInfo } = useClassRole();
  const navigate = useNavigate();

  // Check if this event can be edited by the current user
  const canEditEvent = React.useMemo(() => {
    if (!event) return true;
    
    // If it's not a class event, user can edit their own events
    if (!event.isClassEvent) {
      return event.userId === currentUser?.uid;
    }
    
    // For class events, all class members can edit their own copy
    // We just need to ensure they're part of the same class
    return event.classID === classInfo?.classID;
  }, [event, currentUser, isClassAdmin, classInfo]);
  
  // Check if this is a member editing a class event (not admin)
  const isMemberEditingClassEvent = event?.isClassEvent && !isClassAdmin && event.classID === classInfo?.classID;

  const [userFamilyInfo, setUserFamilyInfo] = useState(null);

  // Update event state when prop changes
  useEffect(() => {
    if (propEvent) {
      setEvent(propEvent);
    }
  }, [propEvent]);

  // Fetch user's family info
  useEffect(() => {
    const fetchFamilyInfo = async () => {
      if (currentUser) {
        const familyInfo = await getUserFamilyInfo(currentUser.uid);
        setUserFamilyInfo(familyInfo);
      }
    };
    fetchFamilyInfo();
  }, [currentUser]);
  
  // Function to update event in Firestore
  const updateEvent = async (updatedEvent) => {
    try {
      console.log("Updating event:", updatedEvent);
      
      // Check if the event has a firestoreId
      const firestoreId = updatedEvent.firestoreId || updatedEvent.id;
      
      // Create a sanitized copy for Firestore
      const { id, firestoreId: _, ...eventData } = updatedEvent;
      
      // Update the document in Firestore
      const eventRef = doc(db, "acceptedEvents", firestoreId);
      await updateDoc(eventRef, eventData);
      
      console.log("Event successfully updated in Firestore");
      return true;
    } catch (error) {
      console.error("Error updating event:", error);
      return false;
    }
  };
  /* RESPONSIVE LAYOUT DETECTION - Adjusts modal display for mobile vs desktop */
  /* Changes form layout and scrolling behavior based on screen size */
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  /* PREDEFINED LOCATION CHIPS - Quick-select buttons for common locations */
  /* Displayed as clickable chips below location input field */
  const [commonLocations, setCommonLocations] = useState([
    { id: 'home', name: 'Home' },
    { id: 'school', name: 'School' }
  ]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');

  /* EVENT CATEGORY BUTTONS - Color-coded category selector buttons */
  /* Displayed as horizontal button group: School, Work, Sport, Life */
  const baseCategories = [
    { id: 'school', name: 'School' },
    { id: 'work', name: 'Work' },
    { id: 'sport', name: 'Sport' },
    { id: 'life', name: 'Life' }
  ];

  // Add class category if user is a class admin
  const categories = isClassAdmin 
    ? [...baseCategories, { id: 'class', name: 'Class' }]
    : baseCategories;

  // If an event has 'other' category, convert it to 'life', keep 'class' if it exists
  const initialCategory = event && event.category ? (event.category === 'other' ? 'life' : event.category) : 'life';

  /* TIME PICKER MODAL STATES - Controls visibility of time selection popups */
  /* Separate modals for start and end time with scroll pickers */
  const [startTimeModalOpen, setStartTimeModalOpen] = useState(false);
  const [endTimeModalOpen, setEndTimeModalOpen] = useState(false);
  const [showParticipantEditor, setShowParticipantEditor] = useState(false);
  const [customParticipants, setCustomParticipants] = useState([]);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [savedParticipantSelection, setSavedParticipantSelection] = useState([]);
  const [isParticipantEditorOpen, setIsParticipantEditorOpen] = useState(false);

  // Initialize with default empty values first
  const [formData, setFormData] = useState({
    eventName: '',
    description: '',
    date: '',
    hasTime: false,
    isAllDay: true,
    startHour: '09',
    startMinute: '00',
    startAmPm: 'AM',
    endHour: '10',
    endMinute: '00',
    endAmPm: 'AM',
    eventDuration: 60, // Default 1 hour
    timeInputValue: '0900', // Added for the time input field
    participants: [],
    location: '',
    category: 'life',
    isRecurring: false,
    recurrencePattern: 'custom', // Always use custom as default
    recurrenceEndDate: '',
    recurrenceCount: 0,
    recurrenceInterval: 1,
    selectedDays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  });

  // Initialize date and time from event
  useEffect(() => {
    // If no event data, do nothing
    if (!event) return;
    
    // Wait for familyParticipants to be loaded
    if (!familyParticipants || familyParticipants.length === 0) {
      console.log('⏳ Waiting for family participants to load...');
      return;
    }

    // Format date to YYYY-MM-DD for input
    const dateStr = `${event.year}-${String(event.month).padStart(2, '0')}-${String(event.day).padStart(2, '0')}`;

    let startHour = '09';
    let startMinute = '00';
    let startAmPm = 'AM';

    // Convert 24-hour to 12-hour format if time exists
    if (event.hasTime && event.hour !== null && event.minute !== null) {
      if (event.hour === 0) {
        startHour = '12';
        startAmPm = 'AM';
      } else if (event.hour < 12) {
        startHour = String(event.hour).padStart(2, '0');
        startAmPm = 'AM';
      } else if (event.hour === 12) {
        startHour = '12';
        startAmPm = 'PM';
      } else {
        startHour = String(event.hour - 12).padStart(2, '0');
        startAmPm = 'PM';
      }
      
      startMinute = String(event.minute).padStart(2, '0');
    }
    
    // Set up end time - either from existing event or calculate based on duration
    let endHour = '10';
    let endMinute = '00';
    let endAmPm = startAmPm; // Default to same AM/PM as start time
    
    if (event.hasTime) {
      // If event has end time directly specified
      if (event.endHour !== undefined && event.endHour !== null) {
        if (event.endHour === 0) {
          endHour = '12';
          endAmPm = 'AM';
        } else if (event.endHour < 12) {
          endHour = String(event.endHour).padStart(2, '0');
          endAmPm = 'AM';
        } else if (event.endHour === 12) {
          endHour = '12';
          endAmPm = 'PM';
        } else {
          endHour = String(event.endHour - 12).padStart(2, '0');
          endAmPm = 'PM';
        }
        
        endMinute = String(event.endMinute || 0).padStart(2, '0');
      } 
      // If event has duration but no end time, calculate end time
      else if (event.eventDuration && event.hour !== null) {
        const startMinutes = (event.hour * 60) + (event.minute || 0);
        const endMinutes = startMinutes + (event.eventDuration || 60);
        
        let endHour24 = Math.floor(endMinutes / 60) % 24;
        const calcEndMinute = endMinutes % 60;
        
        // Convert to 12-hour format
        if (endHour24 === 0) {
          endHour = '12';
          endAmPm = 'AM';
        } else if (endHour24 < 12) {
          endHour = String(endHour24).padStart(2, '0');
          endAmPm = 'AM';
        } else if (endHour24 === 12) {
          endHour = '12';
          endAmPm = 'PM';
        } else {
          endHour = String(endHour24 - 12).padStart(2, '0');
          endAmPm = 'PM';
        }
        
        endMinute = String(calcEndMinute).padStart(2, '0');
      } 
      // Default end time is 1 hour after start time
      else {
        const startHourNum = parseInt(startHour, 10);
        // If start time is 12, end hour depends on AM/PM
        if (startHourNum === 12) {
          if (startAmPm === 'AM') {
            endHour = '01';
            endAmPm = 'PM';
          } else {
            endHour = '01';
            endAmPm = 'AM'; // Next day
          }
        } else if (startHourNum === 11) {
          endHour = '12';
          endAmPm = startAmPm === 'AM' ? 'PM' : 'AM'; // Flip AM/PM
        } else {
          endHour = String(startHourNum + 1).padStart(2, '0');
          endAmPm = startAmPm;
        }
        endMinute = startMinute;
      }
    }
    
    // Parse participants if exist
    const participantList = [];
    console.log('🔍 Loading event participants:', event.participants);
    console.log('🔍 Available family participants:', familyParticipants.map(p => ({ id: p.id, name: p.name, email: p.email })));
    
    // Map old names to new generic roles
    const nameToRoleMapping = {
      'Roger': 'dad',
      'roger': 'dad',
      'Iryna': 'mom',
      'iryna': 'mom',
      'Zeus': 'kid',
      'zeus': 'kid',
      'Rashid-Driver': 'nanny',
      'rashid-driver': 'nanny',
      'Mimi-Nanny': 'nanny',
      'mimi-nanny': 'nanny',
      'Rashid': 'nanny',
      'rashid': 'nanny',
      'Mimi': 'nanny',
      'mimi': 'nanny',
      'Dad': 'dad',
      'dad': 'dad',
      'Mom': 'mom',
      'mom': 'mom',
      'Kid': 'kid',
      'kid': 'kid',
      'Nanny': 'nanny',
      'nanny': 'nanny'
    };
    
    // First check if we have the new participants array format
    if (event.participants && Array.isArray(event.participants)) {
      event.participants.forEach(emailOrName => {
        // First try to match by email (new format)
        const matchByEmail = (familyParticipants || []).find(p => p.email === emailOrName);
        if (matchByEmail && !participantList.includes(matchByEmail.id)) {
          participantList.push(matchByEmail.id);
          console.log(`✓ Matched by email: ${emailOrName} -> ${matchByEmail.name} (${matchByEmail.id})`);
          return;
        }
        
        // Then try to match by ID (in case the participant array contains IDs)
        const matchById = (familyParticipants || []).find(p => p.id === emailOrName);
        if (matchById && !participantList.includes(matchById.id)) {
          participantList.push(matchById.id);
          console.log(`✓ Matched by ID: ${emailOrName} -> ${matchById.name}`);
          return;
        }
        
        // Then check if it's an old name that needs mapping
        const roleId = nameToRoleMapping[emailOrName] || nameToRoleMapping[emailOrName.toLowerCase()];
        if (roleId && !participantList.includes(roleId)) {
          participantList.push(roleId);
          console.log(`✓ Matched by role mapping: ${emailOrName} -> ${roleId}`);
        } else {
          // Also check if it matches any of the new participant names
          const match = (familyParticipants || []).find(p => p.name === emailOrName || p.name.toLowerCase() === emailOrName.toLowerCase());
          if (match && !participantList.includes(match.id)) {
            participantList.push(match.id);
            console.log(`✓ Matched by name: ${emailOrName} -> ${match.name} (${match.id})`);
          } else {
            console.log(`✗ No match found for: ${emailOrName}`);
          }
        }
      });
    } 
    // Fall back to the old participant string format if needed
    else if (event.participant) {
      const participants = event.participant.split(',').map(p => p.trim());
      participants.forEach(name => {
        // Check if it's an old name that needs mapping
        const roleId = nameToRoleMapping[name] || nameToRoleMapping[name.toLowerCase()];
        if (roleId && !participantList.includes(roleId)) {
          participantList.push(roleId);
        } else {
          // Also check if it matches any of the new participant names
          const match = (familyParticipants || []).find(p => p.name === name || p.id === name.toLowerCase());
          if (match && !participantList.includes(match.id)) {
            participantList.push(match.id);
          }
        }
      });
    }
    
    console.log('✅ Final matched participant IDs:', participantList);
    
    setFormData(prev => ({
      ...prev,
      // Basic event details
      eventName: event.eventName || '',
      description: event.description || '',
      date: dateStr,

      // Time settings
      hasTime: event.hasTime || false,
      isAllDay: event.isAllDay || false,
      startHour,
      startMinute,
      startAmPm,
      endHour,
      endMinute,
      endAmPm,
      eventDuration: event.eventDuration || 60,
      timeInputValue: `${startHour}${startMinute}`,
      endTimeInputValue: `${endHour}${endMinute}`,

      // Location and participants
      location: event.location || '',
      participants: participantList,

      // Category
      category: event.category === 'other' ? 'life' : (event.category || 'life'),

      // Recurrence settings
      isRecurring: event.isRecurring || false,
      recurrencePattern: event.recurrencePattern || 'custom',
      recurrenceEndDate: event.recurrenceEndDate || '',
      recurrenceCount: event.recurrenceCount || 0,
      recurrenceInterval: event.recurrenceInterval || 1,
      selectedDays: event.selectedDays || {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
      }
    }));
  }, [event, familyParticipants]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Always show time controls
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      hasTime: true,
      isAllDay: false
    }));
  }, []);

  // Get all available participants (now comes from the hook which includes custom participants)
  const getAllParticipants = () => {
    return familyParticipants || []; // Hook now returns both family and custom participants
  };

  // Add a new custom participant to Firestore
  const addCustomParticipant = async (name, role = 'child') => {
    if (!userFamilyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await addCustomParticipantToFamily(userFamilyInfo.familyId, name, role);
      if (result.success) {
        console.log('Custom participant added successfully');
        // Refresh the participants list immediately
        refreshParticipants();
        return result.participant.id;
      } else {
        console.error('Failed to add custom participant:', result.error);
        alert(result.error || 'Failed to add participant');
      }
    } catch (error) {
      console.error('Error adding custom participant:', error);
    }
  };

  // Remove a custom participant from Firestore
  const removeCustomParticipant = async (participantId) => {
    if (!userFamilyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await removeCustomParticipantFromFamily(userFamilyInfo.familyId, participantId);
      if (result.success) {
        // Remove from form data if selected
        setFormData(prev => ({
          ...prev,
          participants: prev.participants.filter(id => id !== participantId)
        }));
        console.log('Custom participant removed successfully');
        // Refresh the participants list immediately
        refreshParticipants();
      } else {
        console.error('Failed to remove custom participant:', result.error);
      }
    } catch (error) {
      console.error('Error removing custom participant:', error);
    }
  };

  // Remove family member permanently
  const removeFamilyMemberPermanently = async (participantId) => {
    if (!userFamilyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await removeFamilyMember(userFamilyInfo.familyId, participantId);
      if (result.success) {
        // Remove from form data if selected
        setFormData(prev => ({
          ...prev,
          participants: prev.participants.filter(id => id !== participantId)
        }));
        // Refresh the participants list immediately
        refreshParticipants();
        console.log('Family member removed successfully');
      } else {
        console.error('Failed to remove family member:', result.error);
      }
    } catch (error) {
      console.error('Error removing family member:', error);
    }
  };

  // Handle remove button click
  const handleRemoveClick = async (participant) => {
    if (participant.custom) {
      // Remove custom participant immediately
      await removeCustomParticipant(participant.id);
    } else {
      // Show confirmation for family members
      setMemberToRemove(participant);
      setShowRemoveConfirmation(true);
    }
  };

  // Confirm family member removal
  const confirmRemoveMember = async () => {
    if (memberToRemove) {
      await removeFamilyMemberPermanently(memberToRemove.id);
      setShowRemoveConfirmation(false);
      setMemberToRemove(null);
    }
  };

  // Redirect to family share page instead of opening modal
  const openParticipantEditor = () => {
    onClose(); // Close the edit modal first
    navigate('/family-share');
  };

  // Close participant editor and restore selection
  const closeParticipantEditor = () => {
    console.log('🔄 BEFORE Closing participant editor. Current selection:', formData.participants || []);
    console.log('🔄 BEFORE Closing participant editor. Saved selection:', savedParticipantSelection || []);
    
    setShowParticipantEditor(false);
    
    // Restore saved selection immediately
    if (savedParticipantSelection && savedParticipantSelection.length > 0) {
      setFormData(prev => ({
        ...prev,
        participants: [...savedParticipantSelection]
      }));
      console.log('🔄 RESTORED selection to:', savedParticipantSelection);
    }
  };

  const handleParticipantToggle = (participantId) => {
    setFormData(prev => {
      const participants = [...prev.participants];
      
      if (participants.includes(participantId)) {
        return { ...prev, participants: participants.filter(id => id !== participantId) };
      } else {
        return { ...prev, participants: [...participants, participantId] };
      }
    });
  };

  const handleLocationSelect = (locationId) => {
    const location = commonLocations.find(loc => loc.id === locationId);
    if (location) {
      setFormData(prev => ({ ...prev, location: location.name }));
    }
  };
  
  // Function to toggle day selection for custom recurrence
  const toggleDaySelection = (day) => {
    setFormData(prev => ({
      ...prev,
      selectedDays: {
        ...prev.selectedDays,
        [day]: !prev.selectedDays[day]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if user cannot edit this event
    if (!canEditEvent) {
      console.log('❌ User does not have permission to edit this event');
      return;
    }

    // Process the start time
    let startHour = parseInt(formData.startHour, 10);
    let startMinute = parseInt(formData.startMinute, 10);

    if (formData.startAmPm === 'PM' && startHour < 12) {
      startHour += 12;
    } else if (formData.startAmPm === 'AM' && startHour === 12) {
      startHour = 0;
    }

    // Process the end time
    let endHour = parseInt(formData.endHour, 10);
    let endMinute = parseInt(formData.endMinute, 10);

    if (formData.endAmPm === 'PM' && endHour < 12) {
      endHour += 12;
    } else if (formData.endAmPm === 'AM' && endHour === 12) {
      endHour = 0;
    }

    // Calculate event duration in minutes
    let eventDuration = 60; // Default to 1 hour if calculation fails

    if (formData.hasTime) {
      const startTimeMinutes = startHour * 60 + startMinute;
      const endTimeMinutes = endHour * 60 + endMinute;

      // Handle case when end time is on the next day
      if (endTimeMinutes < startTimeMinutes) {
        eventDuration = (24 * 60 - startTimeMinutes) + endTimeMinutes;
      } else {
        eventDuration = endTimeMinutes - startTimeMinutes;
      }

      // If duration is 0 or negative, default to 1 hour
      if (eventDuration <= 0) {
        eventDuration = 60;
      }
    }

    // Parse the date
    const dateParts = formData.date.split('-');
    const eventYear = parseInt(dateParts[0], 10);
    const eventMonth = parseInt(dateParts[1], 10);
    const eventDay = parseInt(dateParts[2], 10);

    // Map participant IDs to names
    // Map participant IDs to emails for filtering (keep emails for event visibility logic)
    const participantEmails = formData.participants.length > 0
      ? formData.participants.map(id => {
          const participant = (familyParticipants || []).find(p => p.id === id);
          return participant ? participant.email : id;
        }).filter(email => email) // Remove any undefined values
      : [];
      
    // Also create participant names for display purposes  
    const participantNames = formData.participants.length > 0
      ? formData.participants.map(id => {
          const participant = (familyParticipants || []).find(p => p.id === id);
          return participant ? participant.name : id;
        })
      : [];

    // Check if this is a new event (has a temporary ID starting with "new-")
    const isNewEvent = event.id && event.id.toString().startsWith('new-');

    // Create updated event object - always preserve id and firestoreId!
    const updatedEvent = {
      // Always include original ID and firestoreId (crucial for updating)
      id: event.id,
      firestoreId: event.firestoreId || event.id,

      // Update with new values
      day: eventDay,
      month: eventMonth,
      year: eventYear,
      hasTime: formData.hasTime,
      hour: formData.hasTime ? startHour : null,
      minute: formData.hasTime ? startMinute : null,
      endHour: formData.hasTime ? endHour : null,
      endMinute: formData.hasTime ? endMinute : null,
      eventDuration: eventDuration, // Duration in minutes
      isAllDay: !formData.hasTime,
      timeText: formData.hasTime ? `${formData.startHour}:${formData.startMinute} ${formData.startAmPm}` : 'All day',
      eventName: formData.eventName,
      description: formData.description,
      location: formData.location || null,
      participant: participantEmails.join(', '), // Use emails for filtering logic
      participants: participantEmails, // Use emails for filtering logic (family role-based event visibility)
      participantNames: participantNames, // Store names for display purposes
      category: formData.category,
      originalDateText: `${eventYear}-${eventMonth}-${eventDay}`,

      // Class event properties
      isClassEvent: formData.category === 'class',
      classID: formData.category === 'class' ? classInfo?.classID : null,
      className: formData.category === 'class' ? classInfo?.className : null,

      // Recurrence properties
      isRecurring: formData.isRecurring,
      recurrencePattern: formData.isRecurring ? formData.recurrencePattern : null,
      recurrenceEndDate: formData.isRecurring && formData.recurrenceEndDate ? formData.recurrenceEndDate : null,
      recurrenceCount: formData.isRecurring ? formData.recurrenceCount : 0,
      recurrenceInterval: formData.isRecurring ? formData.recurrenceInterval : 1,
      selectedDays: formData.isRecurring ? formData.selectedDays : null,

      // Preserve any other important fields from the original event
      createdAt: event.createdAt || new Date().toISOString()
    };

    console.log("Submitting updated event:", updatedEvent);

    // Save the updated event
    if (propOnSave) {
      // Use the provided onSave function if available
      propOnSave(updatedEvent);
    } else {
      // Otherwise, update directly in Firestore
      await updateEvent(updatedEvent);
    }

    // Close the modal
    if (onClose) {
      onClose();
    }
  };


  // Add inline styles to override any potential internal scrolling
  const staticStyles = {
    overflow: isMobile ? 'auto' : 'visible',
    height: 'auto',
    borderRadius: '20px',
    border: '1px solid rgba(0,0,0,0.1)',
    maxHeight: isMobile ? '90vh' : 'none'
  };
  
  // For mobile devices, use more specific styles
  if (isMobile) {
    // Add mobile-specific styles
    Object.assign(staticStyles, {
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    });
  }

  // Combined useEffect for document styling and modal setup
  useEffect(() => {
    // Only run setup when modal is open
    if (isOpen && event) {
      // 1. Create style element if it doesn't exist
      let styleEl = document.getElementById('modal-styles');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'modal-styles';
        styleEl.innerHTML = modalStyles;
        document.head.appendChild(styleEl);
      }

      // 2. Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // 3. Create root portal container for time pickers if it doesn't exist
      let timePickerRoot = document.getElementById('time-picker-root');
      if (!timePickerRoot) {
        timePickerRoot = document.createElement('div');
        timePickerRoot.id = 'time-picker-root';
        timePickerRoot.style.position = 'fixed';
        timePickerRoot.style.top = '0';
        timePickerRoot.style.left = '0';
        timePickerRoot.style.width = '100%';
        timePickerRoot.style.height = '100%';
        timePickerRoot.style.pointerEvents = 'none';
        timePickerRoot.style.zIndex = '9999';
        timePickerRoot.style.display = 'flex';
        timePickerRoot.style.alignItems = 'flex-start';
        timePickerRoot.style.paddingTop = '25vh'; /* Position at roughly 25% from the top */
        timePickerRoot.style.justifyContent = 'center';
        document.body.appendChild(timePickerRoot);
      }

      // Cleanup function
      return () => {
        // 1. Restore body scroll when modal closes
        document.body.style.overflow = '';
        
        // 2. Clean up time picker root if this component created it
        if (timePickerRoot && document.body.contains(timePickerRoot)) {
          document.body.removeChild(timePickerRoot);
        }
      };
    }
  }, [isOpen, event, modalStyles]);
  
  if (!isOpen || !event) {
    return null;
  }
  
  /* MODAL RENDER - Full-screen overlay with centered form */
  /* Adjusts positioning and scrolling based on device type */
  return (
    <>
      <div className="modal-overlay" style={isMobile ? {alignItems: 'flex-start', paddingTop: '5vh'} : {}}>
        <div className="modal-container" style={isMobile ? {maxHeight: '75vh', overflowY: 'auto', marginBottom: '30px'} : {maxHeight: '85vh', overflowY: 'auto'}}>
          <div className="neo-form-header">
            <h3 style={{ fontSize: '16px', color: 'white' }}>
              {canEditEvent ? 'Edit Event' : 'View Event'}
              {isMemberEditingClassEvent && (
                <span style={{ fontSize: '12px', opacity: 0.8, marginLeft: '8px' }}>(Personal copy)</span>
              )}
              {event?.isClassEvent && !canEditEvent && (
                <span style={{ fontSize: '12px', opacity: 0.8, marginLeft: '8px' }}>(Read-only)</span>
              )}
            </h3>
            <button className="modal-close-button" onClick={onClose}>×</button>
          </div>

        <form onSubmit={handleSubmit} className="neo-form-content" style={isMobile ? {overflowY: 'auto', WebkitOverflowScrolling: 'touch', maxHeight: 'calc(75vh - 70px)', paddingBottom: formData.isRecurring ? '40px' : '30px'} : {overflowY: 'auto', WebkitOverflowScrolling: 'touch', maxHeight: '75vh', paddingBottom: formData.isRecurring ? '80px' : '30px'}}>
          <div className="neo-field">
            <label className="neo-label-sm" htmlFor="eventName">Event Name</label>
            <input
              type="text"
              id="eventName"
              name="eventName"
              className="neo-input"
              value={formData.eventName}
              onChange={handleInputChange}
              placeholder="Event Name"
              required
              disabled={!canEditEvent}
              style={!canEditEvent ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            />
          </div>
          
          {/* DATE FIELD - Calendar date picker input */}
          {/* Mobile: Native date picker, Desktop: Styled HTML5 date input */}
          <div className="neo-row">
            <div className="neo-field" style={{flex: '1'}}>
              <label className="neo-label-sm">Date</label>
              <div className="date-field-container">
                {isMobile ? (
                  // Mobile-friendly date input without the calendar icon
                  <input
                    type="date"
                    id="date"
                    name="date"
                    className="neo-input mobile-date-fix"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    disabled={!canEditEvent}
                    style={{
                      fontSize: '14px', 
                      padding: '8px 5px',
                      ...((!canEditEvent) ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {})
                    }} 
                  />
                ) : (
                  // Standard date input for desktop without the calendar icon
                  <input
                    type="date"
                    id="date"
                    name="date"
                    className="neo-input"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    disabled={!canEditEvent}
                    style={!canEditEvent ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  />
                )}
              </div>
            </div>
          </div>

          {/* TIME FIELDS - Start and end time selection buttons */}
          {/* Opens time picker modals when clicked */}
          <div className="neo-row" style={{ marginTop: '10px' }}>
            <div className="neo-field" style={{flex: '1'}}>
              <label className="neo-label-sm">Time</label>
              <div style={{display: 'flex', justifyContent: 'space-between', gap: '6px'}}>
                {/* Start Time - Button to open time picker */}
                <div className="neo-time-button" style={{flex: '1'}}>
                  <button
                    type="button"
                    className="neo-time-picker-button"
                    onClick={() => canEditEvent && setStartTimeModalOpen(true)}
                    disabled={!canEditEvent}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: canEditEvent ? 'white' : '#f5f5f5',
                      border: '1px solid var(--neo-border, #e0e0e0)',
                      borderRadius: '10px',
                      fontSize: '14px',
                      color: 'var(--neo-text, #333)',
                      boxShadow: 'var(--neo-shadow-sm, 0 1px 2px rgba(0,0,0,0.05))',
                      textAlign: 'center',
                      cursor: canEditEvent ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      opacity: canEditEvent ? 1 : 0.6
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    {formData.startHour}:{formData.startMinute} {formData.startAmPm}
                  </button>
                </div>
                
                {/* Separator */}
                <div style={{display: 'flex', alignItems: 'center', padding: '0 2px'}}>
                  <span style={{fontSize: '14px', color: '#666'}}>-</span>
                </div>
                
                {/* End Time - Button to open time picker */}
                <div className="neo-time-button" style={{flex: '1'}}>
                  <button
                    type="button"
                    className="neo-time-picker-button"
                    onClick={() => canEditEvent && setEndTimeModalOpen(true)}
                    disabled={!canEditEvent}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: canEditEvent ? 'white' : '#f5f5f5',
                      border: '1px solid var(--neo-border, #e0e0e0)',
                      borderRadius: '10px',
                      fontSize: '14px',
                      color: 'var(--neo-text, #333)',
                      boxShadow: 'var(--neo-shadow-sm, 0 1px 2px rgba(0,0,0,0.05))',
                      textAlign: 'center',
                      cursor: canEditEvent ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      opacity: canEditEvent ? 1 : 0.6
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    {formData.endHour}:{formData.endMinute} {formData.endAmPm}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          
          {/* CATEGORY SELECTOR - Horizontal button group for event type */}
          {/* Color-coded buttons: School(orange), Work(blue), Sport(red), Life(green) */}
          <div className="neo-field">
            <label className="neo-label-sm">Category</label>
            <div className="neo-category-selector">
              {categories.map(category => (
                <button
                  key={category.id}
                  type="button"
                  className={`neo-category ${category.id} ${formData.category === category.id ? 'active' : ''}`}
                  onClick={() => canEditEvent && setFormData(prev => ({ ...prev, category: category.id }))}
                  disabled={!canEditEvent}
                  style={!canEditEvent ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
          
          {/* LOCATION FIELD - Text input with predefined location chips below */}
          {/* Users can type custom location or click common ones */}
          <div className="neo-field">
            <label className="neo-label-sm">Location</label>
            <div>
              <input
                type="text"
                id="location"
                name="location"
                className="neo-input"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Enter or choose one option below"
                disabled={!canEditEvent}
                style={!canEditEvent ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </div>
            
            <div className="neo-chip-container">
              {commonLocations.map(location => (
                <button
                  key={location.id}
                  type="button"
                  className={`neo-chip ${formData.location === location.name ? 'active' : ''}`}
                  onClick={() => canEditEvent && handleLocationSelect(location.id)}
                  disabled={!canEditEvent}
                  style={!canEditEvent ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  {location.name}
                </button>
              ))}
              <button
                type="button"
                className="neo-chip"
                onClick={() => canEditEvent && setShowAddLocation(true)}
                disabled={!canEditEvent}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: canEditEvent ? 1 : 0.6,
                  cursor: canEditEvent ? 'pointer' : 'not-allowed'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
          
          {/* PARTICIPANTS SECTION - Multi-select chips for event attendees */}
          {/* Toggle chips on/off to add/remove participants */}
          <div className="neo-field">
            <label className="neo-label-sm" style={{ marginBottom: '4px' }}>Who's Involved</label>
            <div className="neo-chip-container">
              {getAllParticipants().map(person => (
                <button
                  key={person.id}
                  type="button"
                  className={`neo-chip person ${formData.participants.includes(person.id) ? 'active' : ''}`}
                  onClick={() => canEditEvent && handleParticipantToggle(person.id)}
                  disabled={!canEditEvent}
                  style={!canEditEvent ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  {person.name}
                  {person.custom && (
                    <span style={{marginLeft: '4px', fontSize: '10px', opacity: 0.7}}>*</span>
                  )}
                </button>
              ))}
              <button
                type="button"
                onClick={() => canEditEvent && openParticipantEditor()}
                className="neo-chip"
                disabled={!canEditEvent}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: canEditEvent ? 1 : 0.6,
                  cursor: canEditEvent ? 'pointer' : 'not-allowed'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Description */}
          <div className="neo-field">
            <textarea
              id="description"
              name="description"
              className="neo-textarea"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Description"
              rows="2"
              disabled={!canEditEvent}
              style={!canEditEvent ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            />
          </div>

          {/* RECURRING EVENT SECTION - Toggle switch with expandable options */}
          {/* When enabled, shows weekday selector and recurrence end options */}
          <div className="neo-field">
            <div 
              className="neo-switch-container" 
              onClick={() => canEditEvent && setFormData(prev => ({ ...prev, isRecurring: !prev.isRecurring }))}
              style={{ 
                position: 'relative',
                cursor: canEditEvent ? 'pointer' : 'not-allowed',
                userSelect: 'none',
                opacity: canEditEvent ? 1 : 0.6,
                ...(isMobile ? {
                  padding: '12px',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease'
                } : {})
              }}
            >
              <label className="neo-switch" onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={e => canEditEvent && setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                  disabled={!canEditEvent}
                />
                <span className="neo-slider"></span>
              </label>
              <span className="neo-switch-label">Recurring Event</span>
            </div>
            
            {formData.isRecurring && (
              <div className="neo-accordion-content">
                <div className="neo-field">
                  <label className="neo-label-sm">Repeat on</label>
                  <div className="neo-weekday-selector" style={isMobile ? {gap: '2px'} : {}}>
                    <button
                      type="button"
                      className={`neo-weekday ${formData.selectedDays.sunday ? 'active' : ''}`}
                      onClick={() => canEditEvent && toggleDaySelection('sunday')}
                      disabled={!canEditEvent}
                      style={{
                        ...(isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}),
                        ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {isMobile ? 'S' : 'Sun'}
                    </button>
                    <button
                      type="button"
                      className={`neo-weekday ${formData.selectedDays.monday ? 'active' : ''}`}
                      onClick={() => canEditEvent && toggleDaySelection('monday')}
                      disabled={!canEditEvent}
                      style={{
                        ...(isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}),
                        ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {isMobile ? 'M' : 'Mon'}
                    </button>
                    <button
                      type="button"
                      className={`neo-weekday ${formData.selectedDays.tuesday ? 'active' : ''}`}
                      onClick={() => canEditEvent && toggleDaySelection('tuesday')}
                      disabled={!canEditEvent}
                      style={{
                        ...(isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}),
                        ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {isMobile ? 'T' : 'Tue'}
                    </button>
                    <button
                      type="button"
                      className={`neo-weekday ${formData.selectedDays.wednesday ? 'active' : ''}`}
                      onClick={() => canEditEvent && toggleDaySelection('wednesday')}
                      disabled={!canEditEvent}
                      style={{
                        ...(isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}),
                        ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {isMobile ? 'W' : 'Wed'}
                    </button>
                    <button
                      type="button"
                      className={`neo-weekday ${formData.selectedDays.thursday ? 'active' : ''}`}
                      onClick={() => canEditEvent && toggleDaySelection('thursday')}
                      disabled={!canEditEvent}
                      style={{
                        ...(isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}),
                        ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {isMobile ? 'T' : 'Thu'}
                    </button>
                    <button
                      type="button"
                      className={`neo-weekday ${formData.selectedDays.friday ? 'active' : ''}`}
                      onClick={() => canEditEvent && toggleDaySelection('friday')}
                      disabled={!canEditEvent}
                      style={{
                        ...(isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}),
                        ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {isMobile ? 'F' : 'Fri'}
                    </button>
                    <button
                      type="button"
                      className={`neo-weekday ${formData.selectedDays.saturday ? 'active' : ''}`}
                      onClick={() => canEditEvent && toggleDaySelection('saturday')}
                      disabled={!canEditEvent}
                      style={{
                        ...(isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}),
                        ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {isMobile ? 'S' : 'Sat'}
                    </button>
                  </div>
                </div>
                
                <div className="neo-field">
                  <label className="neo-label-sm">Ends</label>
                  <div className="neo-radio-group">
                    <label className="neo-radio" style={isMobile ? {display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap', marginBottom: '6px'} : {}}>
                      <input 
                        type="radio"
                        name="endType" 
                        checked={formData.recurrenceCount === 0 && !formData.recurrenceEndDate}
                        onChange={() => canEditEvent && setFormData(prev => ({ 
                          ...prev, 
                          recurrenceCount: 0,
                          recurrenceEndDate: ''  
                        }))}
                        disabled={!canEditEvent}
                        style={isMobile ? {marginRight: '3px'} : {}}
                      />
                      <span className="neo-radio-label" style={isMobile ? {fontSize: '13px'} : {}}>Never</span>
                    </label>
                    
                    <label className="neo-radio" style={isMobile ? {display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap', marginBottom: '6px'} : {}}>
                      <input 
                        type="radio"
                        name="endType"
                        checked={formData.recurrenceCount > 0}
                        onChange={() => canEditEvent && setFormData(prev => ({ 
                          ...prev, 
                          recurrenceCount: prev.recurrenceCount || 10,
                          recurrenceEndDate: ''  
                        }))}
                        disabled={!canEditEvent}
                        style={isMobile ? {marginRight: '3px'} : {}}
                      />
                      <span className="neo-radio-label" style={isMobile ? {fontSize: '13px'} : {}}>After</span>
                      <div style={{display: 'flex', alignItems: 'center'}}>
                        <input 
                          type="number" 
                          min="1" 
                          max="100"
                          disabled={formData.recurrenceCount === 0 || !canEditEvent}
                          value={formData.recurrenceCount || ''}
                          onClick={() => canEditEvent && setFormData(prev => ({ 
                            ...prev, 
                            recurrenceCount: prev.recurrenceCount || 10,
                            recurrenceEndDate: '' 
                          }))}
                          onChange={e => canEditEvent && setFormData(prev => ({ 
                            ...prev, 
                            recurrenceCount: parseInt(e.target.value) || 0,
                            recurrenceEndDate: ''
                          }))}
                          className="neo-input number small"
                          style={{
                            ...(isMobile ? {width: '45px', fontSize: '13px', marginLeft: '5px'} : {}),
                            ...((!canEditEvent) ? { backgroundColor: '#f5f5f5' } : {})
                          }}
                        />
                        <span 
                          className="neo-radio-text" 
                          style={{
                            ...(isMobile ? {fontSize: '12px'} : {}),
                            ...((!canEditEvent) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                          }}
                          onClick={() => canEditEvent && setFormData(prev => ({ 
                            ...prev, 
                            recurrenceCount: prev.recurrenceCount || 10,
                            recurrenceEndDate: '' 
                          }))}
                        >occurrences</span>
                      </div>
                    </label>
                    
                    <label className="neo-radio" style={isMobile ? {display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap'} : {}}>
                      <input 
                        type="radio"
                        name="endType"
                        checked={!!formData.recurrenceEndDate}
                        style={isMobile ? {marginRight: '3px'} : {}}
                        onChange={() => {
                          if (!canEditEvent) return;
                          // Set default end date to 1 month from now
                          const endDate = new Date();
                          endDate.setMonth(endDate.getMonth() + 1);
                          setFormData(prev => ({ 
                            ...prev, 
                            recurrenceCount: 0,
                            recurrenceEndDate: endDate.toISOString().split('T')[0]  
                          }));
                        }}
                        disabled={!canEditEvent}
                      />
                      <span className="neo-radio-label" style={isMobile ? {fontSize: '13px'} : {}}>On</span>
                      <div style={{display: 'flex', alignItems: 'center', flex: 1}}>
                        <input 
                          type="date"
                          disabled={!formData.recurrenceEndDate || !canEditEvent}
                          value={formData.recurrenceEndDate}
                          onClick={() => {
                            if (!canEditEvent || !formData.recurrenceEndDate) {
                              if (canEditEvent) {
                                // Set default end date to 1 month from now
                                const endDate = new Date();
                                endDate.setMonth(endDate.getMonth() + 1);
                                setFormData(prev => ({ 
                                  ...prev, 
                                  recurrenceCount: 0,
                                  recurrenceEndDate: endDate.toISOString().split('T')[0]  
                                }));
                              }
                            }
                          }}
                          onChange={e => canEditEvent && setFormData(prev => ({ 
                            ...prev, 
                            recurrenceEndDate: e.target.value,
                            recurrenceCount: 0
                          }))}
                          className="neo-input date mobile-date-fix"
                          style={{
                            ...(isMobile ? {fontSize: '13px', marginLeft: '5px', width: '130px'} : {}),
                            ...((!canEditEvent) ? { backgroundColor: '#f5f5f5' } : {})
                          }}
                        />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="neo-action-buttons" style={{ marginBottom: '10px' }}>
            {canEditEvent ? (
              <button
                type="submit"
                className="neo-button primary"
              >
                Save Changes
              </button>
            ) : (
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                textAlign: 'center', 
                color: '#6c757d',
                fontSize: '14px',
                marginBottom: '10px'
              }}>
                {event?.isClassEvent ? 'Class events can only be edited by class admins' : 'You can only edit your own events'}
              </div>
            )}
            
            <button
              type="button"
              className="neo-button secondary"
              onClick={onClose}
            >
              {canEditEvent ? 'Cancel' : 'Close'}
            </button>
          </div>
        </form>
      </div>
    </div>
      
      {/* PARTICIPANT EDITOR MODAL - Modal for adding/removing participants */}
      {showParticipantEditor && (
        <div className="modal-overlay" style={{zIndex: 10000}}>
          <div className="modal-container" style={{maxWidth: '400px', maxHeight: '70vh'}}>
            <div className="neo-form-header">
              <h3 style={{ fontSize: '16px', color: 'white' }}>Edit Participants</h3>
              <button className="modal-close-button" onClick={closeParticipantEditor}>×</button>
            </div>
            <div className="neo-form-content" style={{padding: '20px', maxHeight: 'calc(70vh - 70px)', overflowY: 'auto'}}>
              <div style={{marginBottom: '15px'}}>
                <label className="neo-label-sm">Current Participants</label>
                <div style={{marginTop: '8px'}}>
                  {getAllParticipants().map(person => (
                    <div key={person.id} style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px', border: '1px solid #eee', borderRadius: '8px'}}>
                      <span style={{flex: 1}}>
                        {person.name}
                        {person.custom && <span style={{marginLeft: '4px', fontSize: '10px', opacity: 0.7, color: '#666'}}>*</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveClick(person)}
                        style={{
                          background: '#ff4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{marginBottom: '15px'}}>
                <label className="neo-label-sm">Add New Participant</label>
                <div style={{display: 'flex', gap: '8px', marginTop: '8px'}}>
                  <input
                    type="text"
                    placeholder="Enter name"
                    className="neo-input"
                    style={{flex: 1}}
                    onKeyPress={async (e) => {
                      if (e.key === 'Enter') {
                        const name = e.target.value.trim();
                        if (name) {
                          await addCustomParticipant(name);
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={async (e) => {
                      const input = e.target.parentElement.querySelector('input');
                      const name = input.value.trim();
                      if (name) {
                        await addCustomParticipant(name);
                        input.value = '';
                      }
                    }}
                    style={{
                      background: '#2A9D8F',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
              
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px'}}>
                <button
                  type="button"
                  onClick={closeParticipantEditor}
                  className="neo-button secondary"
                  style={{fontSize: '14px', padding: '8px 16px'}}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMATION DIALOG - Confirm family member removal */}
      {showRemoveConfirmation && (
        <div className="modal-overlay" style={{zIndex: 10001}}>
          <div className="modal-container" style={{maxWidth: '350px', maxHeight: '300px'}}>
            <div className="neo-form-header">
              <h3 style={{ fontSize: '16px', color: 'white' }}>Confirm Removal</h3>
            </div>
            <div className="neo-form-content" style={{padding: '20px', textAlign: 'center'}}>
              <p style={{marginBottom: '20px', fontSize: '14px', lineHeight: '1.4'}}>
                Are you sure you want to permanently remove <strong>{memberToRemove?.name}</strong> from your family? 
                This will remove them from all family events and cannot be undone.
              </p>
              <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                <button
                  type="button"
                  onClick={confirmRemoveMember}
                  style={{
                    background: '#ff4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRemoveConfirmation(false);
                    setMemberToRemove(null);
                  }}
                  className="neo-button secondary"
                  style={{fontSize: '14px', padding: '10px 20px'}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TIME PICKER MODALS - Separate popups for start/end time selection */}
      {/* Uses scroll wheels for hour, minute, and AM/PM selection */}
      <TimeInputModal
        isOpen={startTimeModalOpen}
        onClose={() => setStartTimeModalOpen(false)}
        initialHour={formData.startHour}
        initialMinute={formData.startMinute}
        initialAmPm={formData.startAmPm}
        onTimeSelected={({hour, minute, amPm}) => {
          setFormData(prev => ({
            ...prev,
            startHour: hour,
            startMinute: minute,
            startAmPm: amPm
          }));
          setStartTimeModalOpen(false);
        }}
        label="Start Time"
      />
      
      <TimeInputModal
        isOpen={endTimeModalOpen}
        onClose={() => setEndTimeModalOpen(false)}
        initialHour={formData.endHour}
        initialMinute={formData.endMinute}
        initialAmPm={formData.endAmPm}
        onTimeSelected={({hour, minute, amPm}) => {
          setFormData(prev => ({
            ...prev,
            endHour: hour,
            endMinute: minute,
            endAmPm: amPm
          }));
          setEndTimeModalOpen(false);
        }}
        label="End Time"
      />

      {/* ADD LOCATION MODAL */}
      {showAddLocation && (
        <div className="modal-overlay" style={{zIndex: 10001}}>
          <div className="modal-container" style={{maxWidth: '400px'}}>
            <div className="neo-form-header">
              <h3 style={{ fontSize: '16px', color: 'white' }}>Add New Location</h3>
              <button 
                onClick={() => {
                  setShowAddLocation(false);
                  setNewLocationName('');
                }}
                className="neo-close-button"
              >
                ×
              </button>
            </div>
            <div className="neo-form-content" style={{padding: '20px'}}>
              <div className="neo-field">
                <label className="neo-label-sm">Location Name</label>
                <input
                  type="text"
                  className="neo-input"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  placeholder="Enter location name"
                  autoFocus
                />
              </div>
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px'}}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddLocation(false);
                    setNewLocationName('');
                  }}
                  className="neo-button secondary"
                  style={{fontSize: '14px', padding: '8px 16px'}}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (newLocationName.trim()) {
                      const newLocation = {
                        id: newLocationName.toLowerCase().replace(/\s+/g, '-'),
                        name: newLocationName.trim()
                      };
                      setCommonLocations(prev => [...prev, newLocation]);
                      setFormData(prev => ({ ...prev, location: newLocation.name }));
                      setNewLocationName('');
                      setShowAddLocation(false);
                    }
                  }}
                  className="neo-button primary"
                  style={{fontSize: '14px', padding: '8px 16px'}}
                  disabled={!newLocationName.trim()}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditEventModal;